import { FC, useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { Breadcrumbs, Button, Input } from '@mondra/ui-components'
import { useQuery } from '@apollo/client'
import { ISpecialIngredient } from './types'
import { getSpecialIngredientVars } from './helpers'
import { filterEnums, getTableData } from '../../../shared/gridFunctions'
import Table from '../../../components/Table'
import { GET_ALL_SPECIAL_ING } from '../../../query/special-ingredients'
import { EXPORT_SPECIAL_INGREDIENTS_QUERY } from '../../../query/export-special-ingredients'
import { EXPORT_MAX_ROW_COUNT } from '../../../const'
import { ExportGraphqlButton } from '../../../components/ExportGraphqlButton'

const SpecialIngredients: FC = () => {
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<ISpecialIngredient[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [specialIngredient, setSpecialIngredient] = useState<any>(getSpecialIngredientVars)
  const [order, setOrder] = useState([{ class: 'ASC', category: 'ASC', functionalUnit: 'ASC' }])

  const { data, loading, error, refetch } = useQuery(GET_ALL_SPECIAL_ING, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getSpecialIngredientVars,
    },
  })

  const anyRefetch = (variables?: any): Promise<any> => {
    return refetch(variables)
  }

  const setCursors = () => {
    setEndCursor(data.taxonomySpecialFunctionalUnits.pageInfo.endCursor)
    setStartCursor(data.taxonomySpecialFunctionalUnits.pageInfo.startCursor)
  }

  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  useEffect(() => {
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      '/food/special-ingredients/ingredient',
      'taxonomySpecialFunctionalUnits',
      'taxonomySpecialFunctionalUnit'
    )
  }, [data])

  useEffect(() => {
    if (tableSearch != null || !order[0].class) {
      if (!loading) {
        setSpecialIngredient({
          ...filterEnums(tableSearch, getSpecialIngredientVars, data),
        })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getSpecialIngredientVars, data),
          first: 100,
          after: null,
          before: null,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading])

  const exportPayload = useMemo(
    () => ({
      operationName: 'TaxonomySpecialFunctionalUnits',
      query: EXPORT_SPECIAL_INGREDIENTS_QUERY.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: null,
        order,
      },
    }),
    [order]
  )

  return (
    <div className={'packaging'}>
      <div style={{ maxWidth: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs
            routes={[
              {
                path: '/food/special-ingredients',
                title: 'Special Ingredients Table',
              },
            ]}
          />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>Special Ingredients</h2>
        <div className="packaging-actions items-center">
          <Input
            onChange={e => setDebouncedTableSearch(e.target.value)}
            iconType="search"
            placeholder="Search"
          />
          <Button
            onClick={() => navigate('/food/special-ingredients/ingredient?itemId=new')}
            className={'bg-brand-enabled'}
          >
            Add Ingredient
          </Button>
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={specialIngredient || getSpecialIngredientVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default SpecialIngredients
